import { repository } from 'src/jtech/jtech-repository'
import { JtechAxios } from 'src/plugins/axios'

export const IndustrialMapRepository = {
  // ========================================
  // INTERFACE NAMES (ENDPOINTS)
  // ========================================
  nmInterfaceMountIndustrialMap: 'industrialMap.mountIndustrialMap',
  nmInterfaceOutpostDetail: 'outpostCollectionPoint.detalhar',
  nmInterfaceCollectionDetail: 'industrialTaskGroup.detalhar',
  nmInterfaceCollectionPointCollectionDetail: 'industrialMap.detalharColetas',
  nmInterfaceCollectionPointSearch: 'collectionPointIndustrialMap.pesquisar',
  nmInterfaceCollectionPointScheduled: 'collectionPointIndustrialMap.detalharScheduled',
  nmInterfaceIndustrialTaskByCollectionPoint: 'industrialTask.pesquisarByCollectionPoint',
  nmInterfaceIndustrialTaskDetail: 'industrialTask.composicaoDetalhar',
  nmInterfaceIndustrialTaskSearch: 'industrialTask.pesquisar',
  nmInterfaceIndustrialTemporaryStorage: 'industrialTemporaryStorage.pesquisar',
  nmInterfaceIndustrialMapHql: 'industrialMap.hql',
  nmInterfaceIndustrialTaskHql: 'industrialTask.hql',

  // ========================================
  // GRID NAMES (HQL GRIDS)
  // ========================================
  gridFindClients: 'findClients',
  gridFindCollectionPointsOutpost: 'findCollectionPointsOutpost',
  gridFindCollections: 'findCollections',
  gridFindIndustrialTasksByProduct: 'findIndustrialTasksByProduct',
  gridFindIndustrialTasksByCollectionPoint: 'findIndustrialTasksByCollectionPoint',
  gridPesquisa: 'pesquisa',

  taskServiceTypeDefault: '20690001',

  // ========================================
  // MAIN ENDPOINTS (CONSOLIDADOS)
  // ========================================

  /**
   * Monta visão geral com todos os dados da tela inicial
   * @param {Object} params - Parâmetros opcionais
   * @returns {Promise<Object>} Dados consolidados de todos os clientes
   */
  async getVisaoGeral (params = {}) {
    const [clientes, postos, coletas] = await Promise.all([
      this.findClients(),
      this.findCollectionPointsOutpost(),
      this.findCollections()
    ])

    return {
      data: {
        clientes: clientes.data || [],
        dashboard: {
          postosAvancados: postos.data || [],
          coletas: coletas.data || [],
          rondas: [],
          cacambas: [],
          topResiduos: [],
          pontosColeta: []
        },
        resumo: {
          totalColetas: coletas.total || 0,
          totalRondas: 0,
          totalPostos: postos.total || 0,
          totalCacambas: 0,
          totalPontos: 0,
          totalResiduos: 0
        }
      }
    }
  },

  /**
   * Busca dados completos de um cliente específico
   * @param {number} clienteId - ID do cliente
   * @param {Object} params - Parâmetros opcionais (limite, etc)
   * @returns {Promise<Object>} Dados consolidados do cliente
   */
  async getClienteCompleto (clienteId, params = {}) {
    const [postos, coletas] = await Promise.all([
      this.findCollectionPointsOutpost(clienteId),
      this.findCollections(clienteId)
    ])

    return {
      data: {
        cliente: null,
        dashboard: {
          postosAvancados: postos.data || [],
          coletas: coletas.data || [],
          rondas: [],
          cacambas: [],
          topResiduos: [],
          pontosColeta: []
        },
        resumo: {
          totalColetas: coletas.total || 0,
          totalRondas: 0,
          totalPostos: postos.total || 0,
          totalCacambas: 0,
          totalPontos: 0,
          totalResiduos: 0
        }
      }
    }
  },

  // ========================================
  // CORE ENDPOINTS
  // ========================================

  /**
   * Monta objeto completo para exibição do mapa industrial
   * @param {string} startScheduledDate - Data inicial
   * @param {string} endScheduledDate - Data final
   * @param {number} client - ID do cliente
   * @param {Array} taskStatusTypes - Tipos de status das tarefas
   * @returns {Promise<Object>} Dados do mapa industrial
   */
  async mountIndustrialMap (startScheduledDate = null, endScheduledDate = null, client = null, taskStatusTypes = null) {
    const params = { startScheduledDate, endScheduledDate, client, taskStatusTypes }
    return await repository.resolveLogic(this.nmInterfaceMountIndustrialMap, params)
  },

  /**
   * Monta detalhes completos de um posto avançado
   * @param {number} outpostId - ID do posto avançado
   * @returns {Promise<Object>} Detalhes do posto (header + detalhes)
   */
  async mountOutpostDetail (outpostId, filterDate) {
    const params = { idAcaoGrid: outpostId, id: outpostId }
    if (filterDate) {
      // Converter para DD/MM/YYYY se vier em outro formato
      let formattedDate = filterDate
      if (filterDate.includes('-')) {
        // Formato YYYY-MM-DD, converter para DD/MM/YYYY
        const parts = filterDate.split('-')
        formattedDate = `${parts[2]}/${parts[1]}/${parts[0]}`
      }
      // Usar mesma data para início e fim (filtrar apenas aquele dia)
      params.startScheduledDate = formattedDate
      params.endScheduledDate = formattedDate
    }

    const header = repository.resolve(this.nmInterfaceOutpostDetail, params)
    const detalhes = repository.resolveModel(this.nmInterfaceOutpostDetail, params)

    return Promise.all([header, detalhes]).then(([headerData, detalhesData]) => ({
      header: headerData,
      detalhes: detalhesData
    }))
  },

  /**
   * Monta detalhes completos de uma coleta
   * @param {number} collectionId - ID da coleta
   * @returns {Promise<Object>} Detalhes da coleta (header + detalhes)
   */
  async mountCollectionDetail (collectionId) {
    const params = { idAcaoGrid: collectionId, id: collectionId }
    const header = repository.resolve(this.nmInterfaceCollectionDetail, params)
    const detalhes = repository.resolveModel(this.nmInterfaceCollectionDetail, params)

    return Promise.all([header, detalhes]).then(([headerData, detalhesData]) => ({
      header: headerData,
      detalhes: detalhesData
    }))
  },

  /**
   * Monta detalhes de coletas agrupadas por ponto de coleta
   * @param {number} collectionPointId - ID do ponto de coleta
   * @returns {Promise<Object>} Detalhes do ponto de coleta com tarefas (header + detalhes)
   */
  async mountCollectionPointCollectionDetail (collectionPointId) {
    const params = { collectionPoint: collectionPointId }
    const data = await repository.resolveLogic(this.nmInterfaceCollectionPointCollectionDetail, params)
    return {
      header: data,
      detalhes: data
    }
  },

  /**
   * Busca pontos de coleta
   * @param {number} outpostId - ID do posto avançado (opcional)
   * @returns {Promise<Object>} Dados dos pontos de coleta (header + detalhes)
   */
  async mountCollectionPointSearch (outpostId = null) {
    const params = { idAcaoGrid: outpostId, id: outpostId }
    const header = repository.resolve(this.nmInterfaceCollectionPointSearch, params)
    const detalhes = repository.resolveModel(this.nmInterfaceCollectionPointSearch, params)

    return Promise.all([header, detalhes]).then(([headerData, detalhesData]) => ({
      header: headerData,
      detalhes: detalhesData
    }))
  },

  /**
   * Busca detalhes de agendamento de coleta
   * @param {number} outpostId - ID do posto avançado
   * @returns {Promise<Object>} Detalhes do agendamento (header + detalhes)
   */
  async findDetalharScheduled (outpostId) {
    const params = { idAcaoGrid: outpostId, id: outpostId }
    const header = repository.resolve(this.nmInterfaceCollectionPointScheduled, params)
    const detalhes = repository.resolveModel(this.nmInterfaceCollectionPointScheduled, params)

    return Promise.all([header, detalhes]).then(([headerData, detalhesData]) => ({
      header: headerData,
      detalhes: detalhesData
    }))
  },

  // ========================================
  // HQL GRID ENDPOINTS
  // ========================================

  /**
   * Busca clientes usando grid HQL
   * @returns {Promise<Object>} Lista de clientes
   */
  async findClients () {
    return await repository.resolveDatatableModel(this.nmInterfaceIndustrialMapHql, {
      grid: this.gridFindClients
    })
  },

  /**
   * Busca postos avançados usando grid HQL
   * @param {number} clientId - ID do cliente (opcional)
   * @returns {Promise<Object>} Lista de postos avançados
   */
  async findCollectionPointsOutpost (clientId = null) {
    const params = { grid: this.gridFindCollectionPointsOutpost }
    if (clientId) {
      params.client = clientId
    }
    return await repository.resolveDatatableModel(this.nmInterfaceIndustrialMapHql, params)
  },

  /**
   * Busca coletas usando grid HQL
   * @param {number} clientId - ID do cliente (opcional)
   * @returns {Promise<Object>} Lista de coletas
   */
  async findCollections (clientId = null) {
    const params = { grid: this.gridFindCollections }
    if (clientId) {
      params.client = clientId
    }
    return await repository.resolveDatatableModel(this.nmInterfaceIndustrialMapHql, params)
  },

  /**
   * Busca detalhes de resíduos para exibição na modal
   * @param {number} productId - ID do produto/resíduo
   * @returns {Promise<Object>} Detalhes do resíduo com coletas agrupadas por data
   */
  async findResidue (productId = null) {
    return await repository.resolveDatatableModel(this.nmInterfaceIndustrialTaskHql, {
      grid: this.gridFindIndustrialTasksByProduct,
      product: productId
    })
  },

  /**
   * Busca histórico de postos por ponto de coleta
   * @param {number} collectionPoint - ID do ponto de coleta
   * @returns {Promise<Object>} Histórico (header + detalhes + relacionamentos)
   */
  async findHistoryOutposts (collectionPoint) {
    const baseParams = {
      grid: this.gridFindIndustrialTasksByCollectionPoint,
      collectionPoint
    }

    const header = repository.resolve(this.nmInterfaceIndustrialTaskByCollectionPoint, baseParams)
    const detalhes = repository.resolveDatatableModel(this.nmInterfaceIndustrialTaskByCollectionPoint, baseParams)
    const relacionamentos = repository.resolveModel(this.nmInterfaceIndustrialTaskByCollectionPoint, baseParams)

    return Promise.all([header, detalhes, relacionamentos]).then(([headerData, detalhesData, relacionamentosData]) => ({
      header: headerData,
      detalhes: detalhesData,
      relacionamentos: relacionamentosData
    }))
  },

  /**
   * Busca detalhes de uma tarefa industrial com seus itens de acondicionamento
   * @param {number} taskId - ID da tarefa industrial
   * @returns {Promise<Object>} Detalhes da tarefa com industrialTaskItems
   */
  async findIndustrialTaskDetail (taskId) {
    const params = { idAcaoGrid: taskId, id: taskId }
    return await repository.resolveModel(this.nmInterfaceIndustrialTaskDetail, params)
  },

  /**
   * Busca todas as coletas
   * @returns {Promise<Object>} Todas as coletas (header + detalhes)
   */
  async findAllCollections () {
    const baseParams = { grid: this.gridPesquisa }
    const header = repository.resolve(this.nmInterfaceIndustrialTaskSearch, baseParams)
    const detalhes = repository.resolveDatatableModel(this.nmInterfaceIndustrialTaskSearch, baseParams)

    return Promise.all([header, detalhes]).then(([headerData, detalhesData]) => ({
      header: headerData,
      detalhes: detalhesData
    }))
  },

  /**
   * Busca todas as caçambas temporárias
   * @returns {Promise<Object>} Todas as caçambas (header + detalhes)
   */
  async findAllCacambas () {
    const baseParams = { grid: this.gridPesquisa }
    const header = repository.resolve(this.nmInterfaceIndustrialTemporaryStorage, baseParams)
    const detalhes = repository.resolveDatatableModel(this.nmInterfaceIndustrialTemporaryStorage, baseParams)

    return Promise.all([header, detalhes]).then(([headerData, detalhesData]) => ({
      header: headerData,
      detalhes: detalhesData
    }))
  },

  // ========================================
  // FILE DOWNLOAD ENDPOINTS
  // ========================================

  /**
   * Download de imagens dos postos avançados
   * @param {number} outpostId - ID do posto avançado
   * @returns {Promise<Blob>} Download das imagens do posto
   */
  async downloadImagesOutposts (outpostId) {
    if (!outpostId) {
      throw new Error('ID do posto avançado é obrigatório para download das imagens')
    }

    try {
      const response = await JtechAxios.request().get(`collection-point/download/${outpostId}`, {
        responseType: 'blob'
      })
      return response.data
    } catch (error) {
      console.error(`[IndustrialMapRepository] Erro ao baixar imagem do posto ${outpostId}:`, error)
      throw error
    }
  },

  // ========================================
  // ALIAS METHODS (BACKWARD COMPATIBILITY)
  // ========================================

  /**
   * Busca lista de clientes (alias)
   * @param {Object} params - Parâmetros de filtro
   * @returns {Promise<Object>} Lista de clientes
   */
  async getClientes (params = {}) {
    return await this.findClients()
  },

  /**
   * Busca postos avançados (alias)
   * @param {number} clienteId - ID do cliente (opcional)
   * @param {Object} params - Parâmetros adicionais
   * @returns {Promise<Object>} Lista de postos avançados
   */
  async getPostosAvancados (clienteId = null, params = {}) {
    return await this.findCollectionPointsOutpost(clienteId)
  },

  /**
   * Busca coletas (alias)
   * @param {number} clienteId - ID do cliente (opcional)
   * @param {Object} params - Parâmetros adicionais
   * @returns {Promise<Object>} Lista de coletas
   */
  async getColetas (clienteId = null, params = {}) {
    return await this.findCollections(clienteId)
  }
}
